import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Search, Flame, Hash, Target, Sparkles, Heart, Play, ArrowRight, Loader2 } from "lucide-react";
import { AgeGate } from "@/components/AgeGate";
import { BottomNav } from "@/components/BottomNav";
import { ShortsFeed } from "@/pages/shorts/ShortsPages";

const formatCount = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(".0", "") + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(".0", "") + "K";
  return String(n || 0);
};

// Stagger container animation
const fadeStagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
};
const fadeItem = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.65, 0, 0.35, 1] } },
};

const ACCENT_MAP = {
  rose: "from-rose-500 to-amber-400",
  fuchsia: "from-fuchsia-500 to-rose-400",
  amber: "from-amber-500 to-rose-400",
};

const PageShell = ({ title, sectionNum, kicker, icon: Icon, accent = "rose", children }) => (
  <AgeGate>
    <div className="min-h-[100dvh] bg-black text-white pb-32 relative overflow-hidden" data-testid={`page-${title.toLowerCase()}`}>
      <Helmet><title>{`${title} · JGlinks`}</title></Helmet>

      {/* Ambient glows */}
      <div className="pointer-events-none absolute -top-32 -left-20 w-[420px] h-[420px] rounded-full bg-rose-500/15 blur-[120px] animate-glow-breath" />
      <div className="pointer-events-none absolute top-1/3 -right-20 w-[380px] h-[380px] rounded-full bg-amber-500/10 blur-[120px] animate-glow-breath" style={{ animationDelay: "2.5s" }} />

      {/* Sticky header */}
      <header className="sticky top-0 z-30 backdrop-blur-2xl bg-black/70 border-b border-white/[0.06]">
        <div className="max-w-md mx-auto px-5 py-3.5 flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${ACCENT_MAP[accent] || ACCENT_MAP.rose} flex items-center justify-center shadow-[0_8px_28px_-8px_rgba(244,63,94,0.5)]`}>
            <Icon className="w-[18px] h-[18px] text-black" strokeWidth={2.6} />
          </div>
          <div className="flex flex-col">
            <p className="text-[9px] tracking-[0.3em] text-white/35 uppercase leading-none">
              · {sectionNum} / JGlinks
            </p>
            <h1 className="text-base font-black tracking-tight">{title}</h1>
          </div>
        </div>
      </header>

      <main className="relative max-w-md mx-auto px-5 pt-7">
        {/* Editorial section block */}
        <motion.div
          initial="hidden"
          animate="show"
          variants={fadeStagger}
          className="mb-8"
        >
          <motion.p
            variants={fadeItem}
            className="text-[10px] tracking-[0.4em] text-white/35 uppercase mb-3"
          >
            · {sectionNum} / {title}
          </motion.p>
          <motion.h2
            variants={fadeItem}
            className="font-display text-[40px] sm:text-5xl leading-[0.95] font-black tracking-tighter text-white"
          >
            {kicker}
          </motion.h2>
          <motion.div
            variants={fadeItem}
            className="h-px w-16 bg-gradient-to-r from-rose-400 via-amber-300 to-transparent mt-4"
          />
        </motion.div>

        <motion.div initial="hidden" animate="show" variants={fadeStagger}>
          {children}
        </motion.div>
      </main>

      <BottomNav />
    </div>
  </AgeGate>
);

// ============== HOME: Age gate + Shorts feed + Bottom nav ==============
export const HomeShorts = () => {
  return (
    <AgeGate>
      <div className="bg-black text-white" data-testid="home-shorts">
        <Helmet>
          <title>JGlinks — Modelos colombianas en clips +18</title>
          <meta name="description" content="Descubre clips exclusivos de las modelos JGlinks. Shorts, perfiles VIP y contenido premium. Solo +18." />
        </Helmet>
        {/* Shorts feed takes the viewport minus floating bottom nav (~88px) */}
        <div style={{ height: "calc(100dvh - 88px)" }} className="overflow-hidden">
          <ShortsFeed embedded />
        </div>
        <BottomNav />
      </div>
    </AgeGate>
  );
};

// ============== BUSCAR ==============
export const BuscarPage = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const term = q.trim().toLowerCase();
    if (!term) { setResults([]); return; }
    setLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`/api/creators/search?q=${encodeURIComponent(term)}&limit=30`, { signal: ctrl.signal });
        if (r.ok) {
          const d = await r.json();
          setResults(Array.isArray(d) ? d : (d.creators || d.items || []));
        }
      } catch { /* ignore */ }
      finally { setLoading(false); }
    }, 280);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q]);

  return (
    <PageShell title="Buscar" sectionNum="01" kicker="Encuentra a tu favorita." icon={Search} accent="rose">
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Busca por nombre o @usuario…"
          data-testid="buscar-input"
          autoFocus
          className="w-full bg-white/[0.04] border border-white/10 focus:border-rose-400/60 focus:bg-white/[0.06] rounded-2xl pl-11 pr-4 py-3.5 text-white placeholder-white/35 outline-none transition"
        />
      </div>

      {loading && (
        <div className="flex justify-center py-6 text-white/40">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}
      {!loading && q && results.length === 0 && (
        <p className="text-center text-white/40 text-sm py-8">Sin resultados para "{q}"</p>
      )}
      {!q && (
        <div className="text-center py-14">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/5 border border-white/10 mb-4">
            <Search className="w-6 h-6 text-white/35" />
          </div>
          <p className="text-white/50 text-sm">Empieza a escribir para encontrar modelos</p>
          <p className="text-white/25 text-xs mt-1">Tip: prueba un nombre o un @usuario</p>
        </div>
      )}

      <motion.div variants={fadeStagger} className="space-y-1.5">
        {results.map((c) => (
          <motion.div key={c.username || c.id} variants={fadeItem}>
            <Link
              to={`/${c.username}`}
              data-testid={`buscar-result-${c.username}`}
              className="flex items-center gap-3 p-2.5 rounded-2xl border border-transparent hover:border-white/10 hover:bg-white/[0.04] transition group"
            >
              <img
                src={c.avatar || "/logo.png"}
                alt=""
                loading="lazy"
                className="w-12 h-12 rounded-full object-cover ring-1 ring-rose-300/30 group-hover:ring-rose-300/70 transition"
              />
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">{c.name || c.username}</p>
                <p className="text-xs text-white/45 truncate">@{c.username}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-rose-300 group-hover:translate-x-0.5 transition" />
            </Link>
          </motion.div>
        ))}
      </motion.div>
    </PageShell>
  );
};

// ============== TOPS: top shorts by likes ==============
export const TopsPage = () => {
  const [topShorts, setTopShorts] = useState([]);
  const [topCreators, setTopCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("shorts"); // shorts | modelos

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, c] = await Promise.all([
          fetch("/api/shorts/feed?limit=50"),
          fetch("/api/creators?featured=true"),
        ]);
        const sd = await s.json();
        const cd = await c.json();
        const items = (sd.items || []).slice().sort((a, b) => (b.likes || 0) - (a.likes || 0));
        setTopShorts(items);
        setTopCreators(Array.isArray(cd) ? cd : (cd.creators || []));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <PageShell title="Tops" sectionNum="03" kicker="Lo más caliente de la semana." icon={Flame} accent="rose">
      <div className="flex gap-2 mb-6">
        {[
          { key: "shorts", label: "Top Shorts" },
          { key: "modelos", label: "Top Modelos" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tops-tab-${t.key}`}
            className={`px-4 py-2 rounded-full text-[11px] tracking-[0.15em] uppercase font-black transition ${
              tab === t.key
                ? "bg-white text-black shadow-[0_8px_28px_-8px_rgba(255,255,255,0.4)]"
                : "bg-white/[0.04] text-white/55 border border-white/10 hover:bg-white/10"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10 text-white/40"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : tab === "shorts" ? (
        topShorts.length === 0 ? (
          <p className="text-center text-white/40 text-sm py-8">Aún no hay shorts. Vuelve pronto.</p>
        ) : (
          <motion.div variants={fadeStagger} className="grid grid-cols-2 gap-2.5">
            {topShorts.map((s, i) => (
              <motion.div key={s.id} variants={fadeItem}>
                <Link
                  to={`/shorts/${s.id}`}
                  data-testid={`tops-short-${s.id}`}
                  className="relative aspect-[9/16] bg-zinc-900 rounded-2xl overflow-hidden group block"
                >
                  {s.thumbnailUrl ? (
                    <img src={s.thumbnailUrl} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/20 text-3xl">▶</div>
                  )}
                  {i < 3 && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-gradient-to-r from-rose-500 to-amber-400 text-black text-[10px] font-black shadow-lg">
                      #{i + 1}
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 p-2.5 bg-gradient-to-t from-black via-black/70 to-transparent">
                    <p className="text-[11px] font-bold truncate">@{s.creatorUsername}</p>
                    <div className="flex items-center gap-2 text-[10px] text-white/75 mt-0.5">
                      <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" fill="currentColor" />{formatCount(s.likes)}</span>
                      <span className="flex items-center gap-0.5"><Play className="w-3 h-3" fill="currentColor" />{formatCount(s.views)}</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )
      ) : topCreators.length === 0 ? (
        <p className="text-center text-white/40 text-sm py-8">Sin modelos destacadas todavía</p>
      ) : (
        <motion.div variants={fadeStagger} className="space-y-2">
          {topCreators.map((c, i) => (
            <motion.div key={c.username} variants={fadeItem}>
              <Link
                to={`/${c.username}`}
                data-testid={`tops-creator-${c.username}`}
                className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] hover:border-white/15 transition group"
              >
                <span className={`font-display text-3xl font-black w-9 text-center leading-none ${i < 3 ? "bg-gradient-to-b from-rose-300 to-amber-300 bg-clip-text text-transparent" : "text-white/25"}`}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <img src={c.avatar || "/logo.png"} alt="" loading="lazy" className="w-12 h-12 rounded-full object-cover ring-1 ring-rose-300/30 group-hover:ring-rose-300/70 transition" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{c.name || c.username}</p>
                  <p className="text-xs text-white/45">@{c.username}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-rose-300 group-hover:translate-x-0.5 transition" />
              </Link>
            </motion.div>
          ))}
        </motion.div>
      )}
    </PageShell>
  );
};

// ============== HASHTAGS (premium placeholder) ==============
export const HashtagsPage = () => (
  <PageShell title="Hashtags" sectionNum="02" kicker="Explora por tu mood." icon={Hash} accent="fuchsia">
    <div className="text-center py-12">
      <div className="relative inline-block">
        <div className="absolute inset-0 bg-fuchsia-500/20 blur-3xl animate-glow-breath" />
        <div className="relative inline-flex p-6 rounded-3xl bg-gradient-to-br from-fuchsia-500/15 to-purple-500/10 border border-fuchsia-400/20 animate-float-ambient">
          <Hash className="w-12 h-12 text-fuchsia-300" />
        </div>
      </div>
      <p className="text-[10px] tracking-[0.35em] text-white/35 uppercase mt-7 mb-2">· Próximamente</p>
      <h3 className="font-display text-3xl font-black mb-3 tracking-tight">En construcción.</h3>
      <p className="text-white/55 text-sm max-w-sm mx-auto leading-relaxed">
        Pronto vas a poder explorar por hashtags como{" "}
        <span className="text-fuchsia-300 font-semibold">#latina</span>,{" "}
        <span className="text-fuchsia-300 font-semibold">#colombia</span>,{" "}
        <span className="text-fuchsia-300 font-semibold">#fitness</span>{" "}
        y muchos más.
      </p>

      {/* Mock hashtag cloud — purely decorative */}
      <div className="mt-8 flex flex-wrap justify-center gap-2 max-w-xs mx-auto opacity-60">
        {["#latina", "#colombia", "#fitness", "#cosplay", "#playboy", "#premium", "#vip"].map((h, i) => (
          <span
            key={h}
            className="px-3 py-1 rounded-full text-[11px] font-semibold border border-white/10 bg-white/[0.03] text-white/50"
            style={{ animationDelay: `${i * 0.1}s` }}
          >
            {h}
          </span>
        ))}
      </div>
    </div>
  </PageShell>
);

// ============== NICHOS ==============
export const NichosPage = () => {
  const NICHES = [
    { key: "latinas", label: "Latinas", emoji: "🌶", color: "from-rose-500 to-orange-500" },
    { key: "fitness", label: "Fitness", emoji: "💪", color: "from-emerald-500 to-cyan-500" },
    { key: "playboy", label: "Playboy", emoji: "🐰", color: "from-amber-500 to-rose-500" },
    { key: "alternativas", label: "Alternativas", emoji: "🖤", color: "from-purple-500 to-pink-500" },
    { key: "cosplay", label: "Cosplay", emoji: "🎭", color: "from-blue-500 to-purple-500" },
    { key: "premium", label: "Premium VIP", emoji: "👑", color: "from-yellow-500 to-amber-500" },
  ];
  return (
    <PageShell title="Nichos" sectionNum="04" kicker="Tu nicho, tu fantasía." icon={Target} accent="amber">
      <p className="text-white/55 text-sm mb-6 max-w-sm">Encuentra exactamente lo que te gusta. Curado por nuestro equipo.</p>
      <motion.div variants={fadeStagger} className="grid grid-cols-2 gap-3">
        {NICHES.map((n) => (
          <motion.button
            key={n.key}
            variants={fadeItem}
            data-testid={`nicho-${n.key}`}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
            className={`relative aspect-[5/4] rounded-2xl overflow-hidden p-4 text-left bg-gradient-to-br ${n.color} shadow-[0_18px_50px_-20px_rgba(244,63,94,0.4)]`}
            onClick={() => alert("Próximamente — estamos cargando contenido para este nicho")}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
            <span className="relative text-3xl drop-shadow-lg">{n.emoji}</span>
            <p className="absolute bottom-3 left-4 right-4 text-white font-black text-lg drop-shadow leading-tight">{n.label}</p>
            <span className="absolute top-3 right-3 text-[9px] tracking-[0.3em] text-white/70 uppercase">Pronto</span>
          </motion.button>
        ))}
      </motion.div>
      <p className="text-center text-[10px] text-white/30 mt-7 tracking-[0.25em] uppercase">· Más nichos en camino</p>
    </PageShell>
  );
};

// ============== Sparkles unused warning shut-up ==============
const _ = Sparkles;
export { _ };
